import type { Rule, Trait } from './types.js';

/**
 * Regras de validacao do protocolo v1.0.
 *
 * As regras de faixa (RNG:*) e de obrigatoriedade (REQ:*) NAO sao escritas a
 * mao: sao sintetizadas a partir do catalogo de traits, para que catalogo e
 * validacao nunca divirjam. As regras abaixo sao as que exigem conhecimento
 * que o catalogo nao carrega.
 */

// ---------------------------------------------------------------------------
// Sintetizadas a partir do catalogo
// ---------------------------------------------------------------------------

/**
 * Faixa plausivel por trait. Severidade `aviso`, nao `erro`: quem mede e a
 * autoridade sobre a medida, e as faixas da v1.0 ainda sao provisorias. Mas
 * `aviso` exige justificativa digitada — nunca e ignoravel em silencio.
 */
export const synthRangeRules = (traits: Trait[]): Rule[] =>
  traits.map((t) => ({
    kind: 'range',
    id: `RNG:${t.code}`,
    trait: t.code,
    min: t.minPlausible,
    max: t.maxPlausible,
    severity: 'aviso',
    message: `${t.label} fora da faixa plausivel (${t.minPlausible}-${t.maxPlausible} ${t.unit}).`,
  }));

/**
 * Obrigatoriedade por nivel de protocolo. Severidade `erro`: ficha incompleta
 * nao fecha. E o que impede a equipe de sair do haras com buraco no dado.
 */
export const synthRequiredRules = (traits: Trait[]): Rule[] =>
  traits.map((t) => ({
    kind: 'required',
    id: `REQ:${t.code}`,
    trait: t.code,
    when: { levelAtLeast: t.levelMin },
    severity: 'erro',
    message: `${t.label} e obrigatorio a partir do Nivel ${t.levelMin}.`,
  }));

// ---------------------------------------------------------------------------
// Explicitas
// ---------------------------------------------------------------------------

export const explicitRulesV1: Rule[] = [
  /**
   * Consistencia geometrica da folha 3 da ficha:
   *   P_TOR + A_SUB = A_CER  (tolerancia +/- 2 cm)
   * E a unica checagem que pega erro de hipometro ANTES do animal sair da
   * estacao, e por isso e `erro`: bloqueia o fechamento da ficha.
   */
  {
    kind: 'linearIdentity',
    id: 'GEO-001',
    terms: [
      { trait: 'P_TOR', coef: 1 },
      { trait: 'A_SUB', coef: 1 },
    ],
    equals: 'A_CER',
    tolerance: 2,
    unit: 'cm',
    severity: 'erro',
    message:
      'Profundidade toracica + vazio subesternal deveria igualar a altura de cernelha (tolerancia 2 cm). Remedir as tres.',
  },

  /**
   * Proporcoes de sanidade. Nao estao na ficha v1.0 — sao acrescimo nosso para
   * pegar troca de campo (digitar garupa no lugar de cernelha, por exemplo).
   * Faixas provisorias, a reaferir depois dos primeiros ~100 animais.
   */
  {
    kind: 'ratio',
    id: 'RAT-001',
    num: 'A_GAR',
    den: 'A_CER',
    min: 0.94,
    max: 1.08,
    severity: 'aviso',
    message: 'Relacao garupa/cernelha atipica. Conferir se as duas alturas nao foram trocadas.',
  },
  {
    kind: 'ratio',
    id: 'RAT-002',
    num: 'C_COR',
    den: 'A_CER',
    min: 0.92,
    max: 1.22,
    severity: 'aviso',
    message: 'Relacao comprimento do corpo/altura de cernelha atipica. Conferir as duas medidas.',
  },

  // --- Predicados: as regras irregulares, que o vocabulario declarativo nao cobre.
  {
    kind: 'predicate',
    id: 'PRD-001',
    name: 'prenhezExigeMeses',
    severity: 'erro',
    message: 'Femea marcada como prenhe sem o numero de meses de gestacao.',
  },
  {
    kind: 'predicate',
    id: 'PRD-002',
    name: 'claudicacaoBloqueiaDinamica',
    severity: 'erro',
    message:
      'Animal com claudicacao grau AAEP >= 1 nao pode fazer a Estacao 4 (dinamica). Registrar o motivo em observacoes.',
  },
  {
    kind: 'predicate',
    id: 'PRD-003',
    name: 'chegadaRecenteForcaNivel1',
    severity: 'erro',
    message: 'Animal chegado ao haras ha menos de 30 dias so pode ser avaliado no Nivel 1 (censo).',
  },
  {
    kind: 'predicate',
    id: 'PRD-004',
    name: 'ferrageamentoNaoPodeSerFuturo',
    severity: 'erro',
    message: 'Data do ultimo ferrageamento posterior a data da avaliacao.',
  },
  {
    kind: 'predicate',
    id: 'PRD-005',
    name: 'peAbdExigeHora',
    severity: 'erro',
    message:
      'Perimetro abdominal medido sem registro de hora. Sem a hora, o valor nao e comparavel entre animais.',
  },
  {
    kind: 'predicate',
    id: 'PRD-006',
    name: 'duplaMedicaoExigeSegundoAvaliador',
    severity: 'erro',
    message:
      'Animal sorteado para dupla medicao sem uma segunda sessao de medicao com avaliador diferente.',
  },
  /**
   * Estacao 1 da ficha v1.0: peso e ECC tem bolinha, logo fazem parte do censo
   * e de todo nivel acima dele. Nao sao traits (moram em animal_management),
   * por isso predicado e nao `required`.
   */
  {
    kind: 'predicate',
    id: 'PRD-007',
    name: 'pesoEEccObrigatorios',
    severity: 'erro',
    message: 'Peso (com o metodo de pesagem) e escore de condicao corporal (ECC) sao obrigatorios em todos os niveis.',
  },
];
